import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc, getDoc, addDoc, updateDoc,
  collection, serverTimestamp
} from 'firebase/firestore';
import { db } from '@services/firebase';
import { ArrowLeft, Save, Loader2, Plus, X } from 'lucide-react';
import toast from 'react-hot-toast';

const STYLE_OPTIONS = [
  { value: 'default',  label: 'Default (outline/teal hover)' },
  { value: 'featured', label: 'Featured (teal border, highlighted)' },
  { value: 'purple',   label: 'Purple (purple hover)' },
];

const CTA_VARIANTS = [
  { value: 'outline',  label: 'Outline' },
  { value: 'primary',  label: 'Primary (orange)' },
];

export default function AdminPricingEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving]   = useState(false);
  const [newFeature, setNewFeature] = useState('');

  const [form, setForm] = useState({
    name:       '',
    subtitle:   '',
    price:      '',
    period:     '/month',
    features:   [],
    popular:    false,
    style:      'default',
    ctaLabel:   'Request Quote',
    ctaLink:    '/register',
    ctaVariant: 'outline',
    order:      '',
    visible:    true,
    footnote:   '',
  });

  /* ── Load for edit ────────────────────────────────────────────── */
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'pricing', id));
        if (!snap.exists()) {
          toast.error('Plan not found');
          navigate('/admin/pricing');
          return;
        }
        const d = snap.data();
        setForm({
          name:       d.name       || '',
          subtitle:   d.subtitle   || '',
          price:      d.price      || '',
          period:     d.period     ?? '/month',
          features:   d.features   || [],
          popular:    d.popular    || false,
          style:      d.style      || 'default',
          ctaLabel:   d.ctaLabel   || 'Request Quote',
          ctaLink:    d.ctaLink    || '/register',
          ctaVariant: d.ctaVariant || 'outline',
          order:      d.order !== undefined ? String(d.order) : '',
          visible:    d.visible !== false,
          footnote:   d.footnote   || '',
        });
      } catch {
        toast.error('Failed to load plan');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate]);

  /* ── Helpers ──────────────────────────────────────────────────── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const addFeature = () => {
    const f = newFeature.trim();
    if (!f) return;
    setForm(prev => ({ ...prev, features: [...prev.features, f] }));
    setNewFeature('');
  };

  const removeFeature = (idx) =>
    setForm(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== idx) }));

  const moveFeature = (idx, dir) => {
    const arr = [...form.features];
    const swap = idx + dir;
    if (swap < 0 || swap >= arr.length) return;
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    setForm(prev => ({ ...prev, features: arr }));
  };

  /* ── Save ─────────────────────────────────────────────────────── */
  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Plan name is required'); return; }
    if (!form.price.trim()) { toast.error('Price is required'); return; }

    setSaving(true);
    try {
      const payload = {
        name:       form.name.trim(),
        subtitle:   form.subtitle.trim(),
        price:      form.price.trim(),
        period:     form.period.trim(),
        features:   form.features,
        popular:    form.popular,
        style:      form.style,
        ctaLabel:   form.ctaLabel.trim(),
        ctaLink:    form.ctaLink.trim(),
        ctaVariant: form.ctaVariant,
        order:      form.order !== '' ? Number(form.order) : null,
        visible:    form.visible,
        footnote:   form.footnote.trim(),
        updatedAt:  serverTimestamp(),
      };

      if (isEdit) {
        await updateDoc(doc(db, 'pricing', id), payload);
        toast.success('Plan updated!');
      } else {
        payload.createdAt = serverTimestamp();
        await addDoc(collection(db, 'pricing'), payload);
        toast.success('Plan created!');
      }
      navigate('/admin/pricing');
    } catch {
      toast.error('Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32 text-gray-400">
        <Loader2 size={24} className="animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/admin/pricing')}
          className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            {isEdit ? 'Edit Plan' : 'New Plan'}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {isEdit ? 'Update pricing plan details' : 'Create a new pricing plan'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ── Main ─────────────────────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-5">

          {/* Name + Price */}
          <div className="bg-white rounded-2xl shadow-sm p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Plan Name <span className="text-red-400">*</span>
              </label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="e.g. Growth"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Subtitle</label>
              <input
                name="subtitle"
                value={form.subtitle}
                onChange={handleChange}
                placeholder="e.g. Dynamic Web Applications"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Price <span className="text-red-400">*</span>
                  <span className="ml-1 text-gray-400 font-normal text-xs">(e.g. $49 or Custom)</span>
                </label>
                <input
                  name="price"
                  value={form.price}
                  onChange={handleChange}
                  placeholder="$49"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Period
                  <span className="ml-1 text-gray-400 font-normal text-xs">(leave empty for one-time)</span>
                </label>
                <input
                  name="period"
                  value={form.period}
                  onChange={handleChange}
                  placeholder="/month"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-4">
              Features
              <span className="ml-1 text-gray-400 font-normal text-xs">({form.features.length} items)</span>
            </label>

            {/* Add feature row */}
            <div className="flex gap-2 mb-4">
              <input
                value={newFeature}
                onChange={e => setNewFeature(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addFeature()}
                placeholder="Type a feature and press Enter or +"
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={addFeature}
                className="px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm hover:bg-blue-700 transition-colors"
              >
                <Plus size={15} />
              </button>
            </div>

            {/* Feature list */}
            <div className="space-y-2">
              {form.features.map((f, idx) => (
                <div key={idx} className="flex items-center gap-2 group">
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => moveFeature(idx, -1)}
                      disabled={idx === 0}
                      className="text-gray-300 hover:text-gray-500 disabled:opacity-20 leading-none text-xs"
                    >▲</button>
                    <button
                      onClick={() => moveFeature(idx, 1)}
                      disabled={idx === form.features.length - 1}
                      className="text-gray-300 hover:text-gray-500 disabled:opacity-20 leading-none text-xs"
                    >▼</button>
                  </div>
                  <div className="flex-1 px-3 py-2 bg-gray-50 rounded-lg text-sm text-gray-700 border border-gray-100">
                    {f}
                  </div>
                  <button
                    onClick={() => removeFeature(idx)}
                    className="p-1 text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              {form.features.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-4">No features yet — add some above</p>
              )}
            </div>
          </div>

          {/* Footnote */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Footnote
              <span className="ml-1 text-gray-400 font-normal text-xs">(shown below all plans)</span>
            </label>
            <input
              name="footnote"
              value={form.footnote}
              onChange={handleChange}
              placeholder="All plans include SSL, backups, and security…"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* ── Sidebar ──────────────────────────────────────────────── */}
        <div className="space-y-5">

          {/* Visibility + Save */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Settings</h3>

            <label
              className="flex items-center gap-3 cursor-pointer mb-4"
              onClick={() => setForm(prev => ({ ...prev, visible: !prev.visible }))}
            >
              <div className={`relative w-10 h-5 rounded-full transition-colors ${form.visible ? 'bg-green-500' : 'bg-gray-300'}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.visible ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
              <span className="text-sm text-gray-600">{form.visible ? 'Visible on site' : 'Hidden'}</span>
            </label>

            <label
              className="flex items-center gap-3 cursor-pointer mb-5"
              onClick={() => setForm(prev => ({ ...prev, popular: !prev.popular }))}
            >
              <div className={`relative w-10 h-5 rounded-full transition-colors ${form.popular ? 'bg-yellow-400' : 'bg-gray-300'}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.popular ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
              <span className="text-sm text-gray-600">Mark as "Most Popular"</span>
            </label>

            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors"
            >
              {saving
                ? <><Loader2 size={14} className="animate-spin" /> Saving…</>
                : <><Save size={14} /> {isEdit ? 'Update Plan' : 'Create Plan'}</>
              }
            </button>
          </div>

          {/* Style */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Card Style</label>
            <select
              name="style"
              value={form.style}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {STYLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* CTA */}
          <div className="bg-white rounded-2xl shadow-sm p-6 space-y-4">
            <h3 className="text-sm font-semibold text-gray-700">Button (CTA)</h3>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Label</label>
              <input
                name="ctaLabel"
                value={form.ctaLabel}
                onChange={handleChange}
                placeholder="Request Quote"
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Link</label>
              <input
                name="ctaLink"
                value={form.ctaLink}
                onChange={handleChange}
                placeholder="/register"
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Style</label>
              <select
                name="ctaVariant"
                value={form.ctaVariant}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {CTA_VARIANTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          {/* Order */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Sort Order
              <span className="ml-1 text-gray-400 font-normal text-xs">(lower = first)</span>
            </label>
            <input
              name="order"
              type="number"
              value={form.order}
              onChange={handleChange}
              placeholder="1"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
